import 'mapbox-gl/dist/mapbox-gl.css';

import { bbox as turfBbox } from '@turf/turf';
import { debounce } from 'lodash';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type MapMouseEvent,
  MapProvider,
  type MapRef,
  NavigationControl,
  Map as ReactMapGL
} from 'react-map-gl/mapbox';
import { getMapStyle } from 'sonata-static-storage/lib/helpers/map.helpers';

import { CustomLayer } from './CustomLayer/CustomLayer';
import { DataLayer } from './DataLayer/DataLayer';
import { GeoJSONLayer } from './GeoJSONLayer/GeoJSONLayer';
import { fetchCountryBounds } from './mapViewer.resources';
import { PointLayer } from './PointLayer/PointLayer';

import type { Feature, GeoJSON } from 'geojson';
import type { ReactNode } from 'react';
import type { ExtendedLayer, Layer } from 'sonata-static-storage/lib/common/common.types';
import type { LayerMapData } from 'sonata-static-storage/lib/common/itemsGrouping.types';
import type { MapViewerFilters, MapViewerSelectedItems } from './mapViewer.types';

const RESIZE_DEBOUNCE_MS = 100;
const FIT_BOUNDS_OPTIONS = { duration: 800, padding: 40 };

type BBox = [number, number, number, number];

type ViewportInput = {
  latitude: number;
  longitude: number;
  zoom: number;
};

const DEFAULT_VIEWPORT: ViewportInput = {
  latitude: 0,
  longitude: 0,
  zoom: 2
};

type GeoJSONLayerShape = {
  _id: string;
  color: string;
  geoJSON: GeoJSON;
  type: 'geoJSONLayer';
};

function isGeoJSONLayer(layer: Layer): layer is Layer & GeoJSONLayerShape {
  const candidate = layer as Partial<GeoJSONLayerShape>;
  return candidate.type === 'geoJSONLayer' && candidate.geoJSON !== undefined;
}

function finiteBBox(geo: GeoJSON): BBox | null {
  const box = turfBbox(geo);
  return box.length >= 4 && box.slice(0, 4).every(Number.isFinite) ? [box[0], box[1], box[2], box[3]] : null;
}

async function resolveLayerBounds(layer: Layer, layerMapData: LayerMapData[]): Promise<BBox | null> {
  if (isGeoJSONLayer(layer)) {
    return finiteBBox(layer.geoJSON);
  }

  const features: Feature[] = layerMapData
    .filter(item => Boolean(item.polygon))
    .map(item => ({ geometry: item.polygon, properties: {}, type: 'Feature' }));

  if (features.length > 0) {
    return finiteBBox({ features, type: 'FeatureCollection' });
  }

  if (layer.config?.country) {
    return fetchCountryBounds(layer.config.country);
  }

  return null;
}

function unionBBox(boxes: Array<BBox | null>): BBox | null {
  const valid = boxes.filter((box): box is BBox => box !== null);
  if (valid.length === 0) {
    return null;
  }
  return valid.reduce<BBox>(
    (acc, box) => [
      Math.min(acc[0], box[0]),
      Math.min(acc[1], box[1]),
      Math.max(acc[2], box[2]),
      Math.max(acc[3], box[3])
    ],
    [Infinity, Infinity, -Infinity, -Infinity]
  );
}

export type TooltipFeature = {
  id?: string | number;
  properties?: Record<string, unknown>;
  state?: Record<string, unknown>;
};

type Props = {
  filters?: MapViewerFilters;
  fitBounds?: BBox;
  initialViewport?: ViewportInput;
  layer?: Layer | null;
  layers?: Layer[];
  layerMapData?: LayerMapData[];
  renderTooltip?: (feature: TooltipFeature) => ReactNode;
  selectedItems?: MapViewerSelectedItems;
  style?: 'dark' | 'light';
};

function renderLayer(
  layer: Layer | null | undefined,
  filters: MapViewerFilters,
  selectedItems: MapViewerSelectedItems,
  layerMapData: LayerMapData[]
): JSX.Element | null {
  if (!layer) {
    return null;
  }

  if (isGeoJSONLayer(layer)) {
    return <GeoJSONLayer color={layer.color} geoJSON={layer.geoJSON} id={layer._id} />;
  }

  switch (layer.config?.type) {
    case 'index':
      return <DataLayer filters={filters} layer={layer} layerMapData={layerMapData} selectedItems={selectedItems} />;
    case 'venuesLayer':
    case 'screensLayer':
      return (
        <PointLayer
          filters={filters}
          layer={layer as ExtendedLayer}
          layerMapData={layerMapData}
          selectedItems={selectedItems}
        />
      );
    case 'locationsLayer':
      return (
        <CustomLayer
          filters={filters}
          layer={layer as ExtendedLayer}
          layerMapData={layerMapData}
          selectedItems={selectedItems}
        />
      );
    default:
      return null;
  }
}

export function MapViewer({
  filters = {},
  fitBounds,
  initialViewport = DEFAULT_VIEWPORT,
  layer = null,
  layers,
  layerMapData = [],
  renderTooltip,
  selectedItems = {},
  style = 'dark'
}: Props): JSX.Element {
  const mapRef = useRef() as React.MutableRefObject<MapRef>;
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [tooltip, setTooltip] = useState<{ node: ReactNode; x: number; y: number } | null>(null);

  const resolvedLayers = useMemo<Layer[]>(() => layers ?? (layer ? [layer] : []), [layers, layer]);

  const interactiveLayerIds = useMemo<string[] | undefined>(
    () =>
      renderTooltip
        ? resolvedLayers
            .filter(currentLayer => currentLayer.config?.type === 'index' && currentLayer.config?._id)
            .map(currentLayer => `layer_${currentLayer.config?._id}`)
        : undefined,
    [renderTooltip, resolvedLayers]
  );

  const handleMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (!renderTooltip) {
        return;
      }
      const feature = event.features?.[0];
      const node = feature
        ? renderTooltip({
            id: feature.id,
            properties: feature.properties ?? undefined,
            state: (feature as { state?: Record<string, unknown> }).state
          })
        : null;
      setTooltip(node ? { node, x: event.point.x, y: event.point.y } : null);
    },
    [renderTooltip]
  );

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  useEffect(() => {
    if (!mapWrapperRef.current) {
      return;
    }
    const resizer = new ResizeObserver(debounce(() => mapRef.current?.resize(), RESIZE_DEBOUNCE_MS));
    resizer.observe(mapWrapperRef.current);
    return () => {
      resizer.disconnect();
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) {
      return;
    }

    if (fitBounds) {
      map.fitBounds(
        [
          [fitBounds[0], fitBounds[1]],
          [fitBounds[2], fitBounds[3]]
        ],
        FIT_BOUNDS_OPTIONS
      );
      return;
    }

    if (resolvedLayers.length === 0) {
      return;
    }
    let cancelled = false;
    Promise.all(resolvedLayers.map(currentLayer => resolveLayerBounds(currentLayer, layerMapData))).then(boxes => {
      const bounds = unionBBox(boxes);
      if (cancelled || !bounds) {
        return;
      }
      map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]]
        ],
        FIT_BOUNDS_OPTIONS
      );
    });
    return () => {
      cancelled = true;
    };
  }, [fitBounds, resolvedLayers, layerMapData, isMapLoaded]);

  return (
    <MapProvider>
      <div ref={mapWrapperRef} style={{ height: '100%', position: 'relative', width: '100%' }}>
        <ReactMapGL
          attributionControl={false}
          id="syncMap"
          initialViewState={initialViewport}
          {...(interactiveLayerIds && { interactiveLayerIds })}
          logoPosition="bottom-left"
          mapStyle={getMapStyle(style)}
          mapboxAccessToken={taptap.config.mapbox.accessToken}
          onLoad={() => setIsMapLoaded(true)}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
          ref={mapRef}
        >
          <NavigationControl position="top-right" showCompass showZoom visualizePitch />
          {resolvedLayers.map(currentLayer => (
            <Fragment key={currentLayer.config?._id ?? (currentLayer as Layer & { _id?: string })._id}>
              {renderLayer(currentLayer, filters, selectedItems, layerMapData)}
            </Fragment>
          ))}
        </ReactMapGL>
        {tooltip && (
          <div
            className="mapViewer__tooltip"
            style={{
              background: '#1f2d3d',
              borderRadius: 4,
              color: '#fff',
              fontSize: 12,
              left: tooltip.x,
              lineHeight: 1.35,
              padding: '6px 10px',
              pointerEvents: 'none',
              position: 'absolute',
              top: tooltip.y,
              transform: 'translate(-50%, calc(-100% - 12px))',
              whiteSpace: 'nowrap',
              zIndex: 2
            }}
          >
            {tooltip.node}
          </div>
        )}
      </div>
    </MapProvider>
  );
}
